import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Shield, ArrowRight, CheckCircle, Clock, FileText, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import SectionRenderer from '@/components/sections/SectionRenderer';
import { getBrandImages, getBrandCategory } from '@/components/brandImages';

const CATEGORIES = [
  { slug: 'handbags', label: 'Handbags' },
  { slug: 'watches', label: 'Watches' },
  { slug: 'shoes', label: 'Shoes' },
  { slug: 'clothing', label: 'Clothing' },
  { slug: 'accessories', label: 'Accessories' },
  { slug: 'jewelry', label: 'Jewelry' },
];

export default function BrandDetailPage() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/catalog/brands/${slug}`)
      .then(r => setBrand(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const brandPage = brand?.page;
  useSeoMeta(brandPage
    ? { seo_title: brandPage.seo_title, seo_description: brandPage.seo_description }
    : { seo_title: brand?.name ? `${brand.name} Authentication - The Fashion Detective` : '' }
  );

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
    </div>
  );

  if (!brand) return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 text-center">
      <h1 className="font-display text-2xl font-medium text-ink mb-2">Brand Not Found</h1>
      <Link to="/brands"><Button variant="outline" size="sm"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Brands</Button></Link>
    </div>
  );

  const images = (() => {
    const custom = (brandPage?.media_refs || [])
      .filter(m => m.type === 'image' && m.url)
      .map(m => m.url);
    if (custom.length > 0) return [0, 1, 2, 3].map(i => custom[i % custom.length]);
    return getBrandImages(slug);
  })();
  const productCategory = getBrandCategory(slug);

  return (
    <div data-testid={`brand-detail-${slug}`}>
      {/* Hero with image */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-20">
          <Link to="/brands" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> All Brands
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{productCategory}</p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.02em] text-ink mb-3" data-testid="brand-detail-name">
                {brandPage?.hero_title || brand.name}
              </h1>
              {brandPage?.hero_subtitle && (
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl mb-6">{brandPage.hero_subtitle}</p>
              )}
              <div className="flex flex-wrap gap-3">
                <Link to="/order/submit">
                  <Button className="h-11 px-6 rounded-[12px] gap-2 shadow-[0_12px_26px_rgba(14,14,16,0.10)] active:scale-[0.98]" data-testid="brand-detail-cta">
                    Authenticate {brand.name} <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/services/appraisal">
                  <Button variant="outline" className="h-11 px-6 rounded-[12px] gap-2 active:scale-[0.98]">
                    <FileText className="h-4 w-4" /> Get Appraisal
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Hero image collage */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex gap-3 justify-end"
            >
              <div className="flex flex-col gap-3">
                <div className="w-44 h-52 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                  <img src={images[0]} alt={brand.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-44 h-32 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                  <img src={images[1]} alt={brand.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="flex flex-col gap-3 mt-6">
                <div className="w-44 h-40 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                  <img src={images[2]} alt={brand.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-44 h-28 rounded-2xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg, #0E6B5D 0%, #0a5449 100%)' }}>
                  <div className="h-full flex flex-col items-center justify-center p-3">
                    <Shield className="h-5 w-5 text-white mb-1" strokeWidth={1.5} />
                    <p className="text-white text-[10px] font-semibold uppercase tracking-wider">{brand.name}</p>
                    <p className="text-emerald-200 text-[9px] uppercase tracking-widest">Authenticated</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image gallery strip — mobile only */}
      <section className="py-8 sm:hidden" style={{ background: 'hsl(var(--bg-paper-2))' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
            {images.map((img, i) => (
              <div key={i} className="w-40 h-40 rounded-xl overflow-hidden flex-shrink-0 snap-start shadow-sm">
                <img src={img} alt={`${brand.name} ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-8 border-b border-stone/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: Shield, text: 'Expert Authentication' },
              { icon: Clock, text: '1 Business Day' },
              { icon: CheckCircle, text: 'Verified Certificate' },
              { icon: Camera, text: 'Photo-Based Review' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-2 text-muted-foreground">
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-medium">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Body content with side image */}
      {brandPage?.body_content && (
        <section className="py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: brandPage.body_content }} />
              {/* Side image stack */}
              <div className="flex flex-col gap-4">
                <div className="rounded-xl overflow-hidden shadow-sm">
                  <img src={images[3] || images[0]} alt={brand.name} className="w-full h-48 object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-sm">
                  <img src={images[1]} alt={brand.name} className="w-full h-48 object-cover" loading="lazy" />
                </div>
                <Card className="border-stone/50">
                  <CardContent className="p-4 text-center">
                    <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#0E6B5D' }} />
                    <p className="text-xs font-medium text-ink mb-1">Authenticate Your {brand.name}</p>
                    <p className="text-[10px] text-muted-foreground mb-3">Starting at $20 per item</p>
                    <Link to="/order/submit">
                      <Button size="sm" className="w-full text-xs gap-1 rounded-lg">
                        Get Started <ArrowRight className="h-3 w-3" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Brand page sections if any */}
      {brandPage?.sections && brandPage.sections.length > 0 && (
        <SectionRenderer sections={brandPage.sections} />
      )}

      {/* Full-width image banner */}
      <section className="relative h-48 sm:h-64 overflow-hidden">
        <img src={images[2]} alt={brand.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 w-full">
            <h2 className="font-display text-xl sm:text-2xl font-medium text-white mb-2">Need {brand.name} Authenticated?</h2>
            <p className="text-white/70 text-sm mb-4">Our experts are ready to verify your item.</p>
            <Link to="/order/submit">
              <Button className="rounded-[12px] gap-2 active:scale-[0.98]">
                Start Now <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories CTA */}
      <section className="py-12 sm:py-16" style={{ background: 'hsl(var(--bg-paper-2))' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-xl sm:text-2xl font-medium text-ink mb-6">Authenticate by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map(cat => (
              <Link key={cat.slug} to={`/categories/${cat.slug}`} className="no-underline">
                <Card className="bg-white border-stone/50 hover:shadow-[var(--shadow-pop)] transition-shadow duration-200 text-center">
                  <CardContent className="p-4">
                    <span className="text-sm font-medium text-ink">{cat.label}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
